<template>
  <v-layout wrap>
    <v-flex xs12>
      <v-toolbar dense extended color="primary" dark style="padding-top:50px">
        <v-flex xs12 pa-5>
          <v-toolbar-title slot="extension" class="display-2">
            <v-icon x-large style="padding-bottom:10px">add_a_photo</v-icon>
            Station Photo Upload
          </v-toolbar-title>
          Upload photos to station photo configurations.
          <v-btn flat dark :to="{ name: 'ingestionDashboard' }" class="mt-2">
            <v-icon left>arrow_back</v-icon>Back to Dashboard
          </v-btn>
        </v-flex>
      </v-toolbar>
    </v-flex>
    <v-flex xs12 md8 offset-md2 pa-3>
      <v-card>
        <v-card-text class="pt-3">
          <v-form ref="form" lazy-validation>
            <v-select label="Station Photo Configuration" v-model="selectedStation"
              :items="photoStationIds" item-text="text" item-value="value"
              :rules="[rules.required]" required
              :hint="photoConfigsLoaded ? 'Select an active StationPhotos configuration' : 'No active StationPhotos configurations found. Create one first.'"
              :persistent-hint="!photoConfigsLoaded"></v-select>
            <v-text-field label="Job ID (optional)" v-model="jobId"
              placeholder="Leave empty to auto-generate"
              hint="UUID to correlate with previous uploads"></v-text-field>
            <v-layout row wrap align-center>
              <v-flex xs12 sm6>
                <input ref="fileInput" type="file" multiple accept=".jpg,.jpeg,.png,.gif,.bmp,.tiff,.tif"
                  @change="onFilesSelected" style="display:none">
                <v-btn color="primary" @click="$refs.fileInput.click()" :disabled="!selectedStation">
                  <v-icon left>attach_file</v-icon>Select Photos
                </v-btn>
              </v-flex>
              <v-flex xs12 sm6>
                <span v-if="selectedFiles.length > 0" class="body-2">
                  {{ selectedFiles.length }} file(s) selected
                </span>
              </v-flex>
            </v-layout>
            <v-layout row wrap v-if="previews.length > 0" class="mt-3">
              <v-flex xs4 sm3 md2 v-for="(p, i) in previews" :key="i" pa-1>
                <v-card><v-img :src="p" height="80" contain></v-img>
                  <v-card-text class="caption pa-1 text-xs-center">{{ fileNames[i] }}</v-card-text></v-card>
              </v-flex>
            </v-layout>
            <v-progress-linear v-if="uploading" indeterminate class="mt-3"></v-progress-linear>
            <v-btn block large color="success" dark @click="doUpload"
              :disabled="!selectedStation || selectedFiles.length === 0 || uploading" class="mt-3">
              <v-icon left>cloud_upload</v-icon>Upload Photos
            </v-btn>
          </v-form>
        </v-card-text>

        <!-- Results -->
        <v-card-text v-if="result">
          <v-divider class="mb-3"></v-divider>
          <v-subheader>Upload Results</v-subheader>
          <v-layout row>
            <v-flex xs4><v-chip color="success" dark>Uploaded: {{ result.uploadedCount }}</v-chip></v-flex>
            <v-flex xs4><v-chip color="warning" dark>Skipped: {{ result.skippedCount }}</v-chip></v-flex>
            <v-flex xs4><v-chip color="error" dark>Failed: {{ result.failedCount }}</v-chip></v-flex>
          </v-layout>
          <v-data-table v-if="result.details && result.details.length > 0"
            :headers="detailHeaders" :items="result.details" hide-actions class="mt-2">
            <template slot="items" slot-scope="props">
              <td>{{ props.item.fileName }}</td>
              <td><v-chip :color="detailStatusColor(props.item.status)" small dark>{{ props.item.status }}</v-chip></td>
              <td>{{ props.item.errorMessage || (props.item.blobUrl ? props.item.blobUrl.substring(0, 60) + '...' : '-') }}</td>
            </template>
          </v-data-table>
          <v-btn color="primary" @click="resetForm" class="mt-2">Upload More</v-btn>
        </v-card-text>
      </v-card>
    </v-flex>

    <v-snackbar :color="snackColor" :timeout="6000" top v-model="snackbar">
      {{ snackMessage }}
      <v-btn dark flat @click="snackbar = false">Close</v-btn>
    </v-snackbar>
  </v-layout>
</template>

<script>
import { createNamespacedHelpers } from 'vuex'
const ingestion = createNamespacedHelpers('ingestionManagement')

export default {
  data() {
    return {
      selectedStation: null,
      jobId: '',
      selectedFiles: [],
      fileNames: [],
      previews: [],
      uploading: false,
      result: null,
      snackbar: false, snackColor: 'error', snackMessage: '',
      detailHeaders: [
        { text: 'File', value: 'fileName', align: 'left' },
        { text: 'Status', value: 'status', align: 'left' },
        { text: 'Details', value: 'blobUrl', align: 'left' },
      ],
      rules: { required: v => !!v || 'Required' },
    }
  },
  computed: {
    ...ingestion.mapGetters(['configurations', 'stationPhotoTypeConfiguration']),
    photoStationIds() {
      return this.configurations
        .filter(c => c.DataSourceType === this.stationPhotoTypeConfiguration.value && c.IsActive)
        .map(c => ({ text: c.StationId, value: c.StationId }))
    },
    photoConfigsLoaded() {
      return this.configurations.some(c => c.DataSourceType === this.stationPhotoTypeConfiguration.value && c.IsActive)
    },
  },
  methods: {
    ...ingestion.mapActions(['fetchConfigurations', 'uploadPhotos']),
    onFilesSelected(e) {
      this.selectedFiles = Array.from(e.target.files)
      this.fileNames = this.selectedFiles.map(f => f.name)
      this.previews = []
      this.selectedFiles.forEach(f => {
        const reader = new FileReader()
        reader.onload = (ev) => this.previews.push(ev.target.result)
        reader.readAsDataURL(f)
      })
    },
    doUpload() {
      if (!this.selectedStation || this.selectedFiles.length === 0) return
      this.uploading = true
      const formData = new FormData()
      formData.append('stationName', this.selectedStation)
      if (this.jobId) formData.append('jobId', this.jobId)
      this.selectedFiles.forEach(f => formData.append('photos', f))
      this.uploadPhotos({ formData, successCb: (data) => {
        this.result = data
        this.uploading = false
      }, failedCb: (err) => {
        this.uploading = false
        this.snackMessage = err.response ? (err.response.data.error || err.response.data) : err.message
        this.snackColor = 'error'; this.snackbar = true
      }})
    },
    resetForm() {
      this.result = null; this.selectedFiles = []; this.fileNames = []
      this.previews = []; this.jobId = ''
    },
    detailStatusColor(s) {
      return { Uploaded: 'success', Skipped: 'warning', Failed: 'error' }[s] || 'grey'
    },
  },
  mounted() { this.fetchConfigurations({ isActive: true }) },
}
</script>
